/**
 * OverlayMarker - Exact replica of CustomMarker design using OverlayView
 *
 * This recreates the exact CustomMarker design with image background and proper styling
 */

// Factory function to create OverlayMarker when Google Maps is available
export function createOverlayMarker(markerData, options = {}) {
  if (typeof google === 'undefined' || !google.maps) {
    throw new Error('Google Maps is not available')
  }

  class CustomDesignOverlayMarker extends google.maps.OverlayView {
    constructor(markerData, options = {}) {
      super()

      this.markerData = markerData
      this.options = options
      this.div = null
      this.showTooltip = false

      // Bind methods
      this.handleClick = this.handleClick.bind(this)
      this.handleDocumentClick = this.handleDocumentClick.bind(this)
    }

    /**
     * Called when the overlay is added to the map
     */
    onAdd() {
      // Create the main container div
      this.div = document.createElement('div')
      this.div.style.position = 'absolute'
      this.div.style.cursor = 'pointer'

      // Create marker content with exact CustomMarker design
      this.createMarkerContent()

      // Add click handler
      this.div.addEventListener('click', this.handleClick)

      // Add to the map's pane
      const panes = this.getPanes()
      if (panes) {
        panes.overlayMouseTarget.appendChild(this.div)
      }
    }

    /**
     * Called when the overlay is removed from the map
     */
    onRemove() {
      if (this.div && this.div.parentNode) {
        this.div.parentNode.removeChild(this.div)
      }
      document.removeEventListener('click', this.handleDocumentClick)
    }

    /**
     * Called when the overlay needs to be drawn
     */
    draw() {
      if (!this.div) return

      const projection = this.getProjection()
      if (!projection) return

      const position = this.markerData.position
      if (!position || !position.lat || !position.lng) return

      try {
        const latLng = new google.maps.LatLng(position.lat, position.lng)
        const point = projection.fromLatLngToDivPixel(latLng)

        if (point) {
          // Position the marker exactly like CustomMarker
          this.div.style.left = `${point.x}px`
          this.div.style.top = `${point.y}px`
          this.div.style.transform = 'translate(-50%, -50%)'
          this.div.style.display = 'block'
        }
      } catch (error) {
        console.warn('Error drawing overlay marker:', error)
      }
    }

    /**
     * Create marker content - Exact replica of CustomMarker design
     */
    createMarkerContent() {
      // Vérification de sécurité : si this.div n'existe pas, ne rien faire
      if (!this.div) {
        console.warn('createMarkerContent appelé mais this.div est null')
        return
      }

      const isUser = this.markerData.isUserPosition
      const iconType = this.markerData.iconType
      const tooltipText =
        this.markerData.content ||
        this.markerData.options?.title ||
        "Point d'intérêt"

      let markerHTML =
        '<div class="custom-marker absolute z-50 pointer-events-auto flex items-center justify-center w-0 h-0">'

      // Position utilisateur
      if (isUser) {
        markerHTML += `
          <div class="absolute">
            <div class="w-12 h-12 p-3 rounded-full bg-blue-500 border-blue-600 flex items-center justify-center transition-transform duration-200 user-heading-rotation">
              <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z" />
              </svg>
            </div>
            <div class="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
          </div>
        `
      } else {
        // Conteneur principal avec image de fond
        let bgColor = ''
        if (iconType === 'rider') bgColor = '#f97316' // orange-500
        else if (iconType === 'restaurant')
          bgColor = '#f97316' // orange-500
        else if (iconType === 'pickup')
          bgColor = '#f97316' // orange-500
        else if (iconType === 'checkpoint')
          bgColor = '#3b82f6' // blue-500
        else if (iconType === 'destination')
          bgColor = '#ef4444' // red-500
        else bgColor = '#a855f7' // purple-500

        markerHTML += `
          <div class="absolute bottom-0 w-12">
            <div
              class="w-full"
              style="
                mask-size: 100% 100%; 
                background-color: ${bgColor};
                mask-image: url('/images/map/map-location-container.png');
                -webkit-mask-image: url('/images/map/map-location-container.png');
              }"
            >
              <img src="/images/map/map-location-container.png" class="w-12" style="opacity: 0 !important"  />
            </div>
            <div class="absolute py-[50%] w-full z-[2] top-0 left-0">
              <div class="absolute w-full h-full p-[1px] top-0">
        `

        // Contenu intérieur selon le type
        if (iconType === 'restaurant') {
          markerHTML += `
            <div class="relative">
              <div class="w-full h-full p-2 rounded-full flex items-center justify-center" style="background-color: #f97316;">
                <svg class="w-full h-full text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
                </svg>
              </div>
            </div>
          `
        } else if (iconType === 'pickup') {
          markerHTML += `
            <div class="relative">
              <div class="w-full h-full p-1 rounded-full flex items-center justify-center" style="background-color: #f97316;">
                <div
                  class="h-full w-full"
                  style="
                    mask-size: 100% 100%; 
                    background-color: white;
                    mask-image: url('/images/map/flag.png');
                    -webkit-mask-image: url('/images/map/flag.png');
                  }"
                >
                  <img src="/images/map/flag.png" class="w-full" style="opacity: 0 !important"  />
                </div>
              </div>
            </div>
          `
        } else if (iconType === 'rider') {
          markerHTML += `
            <div class="relative">
              <div class="w-full h-full p-2 rounded-full flex items-center justify-center" style="background-color: #f97316;">
                <img src="/images/map/scooter-white.svg" class="w-full h-full"  />
              </div>
            </div>
          `
        } else if (iconType === 'checkpoint') {
          markerHTML += `
            <div class="relative">
              <div class="w-full h-full p-1.5 rounded-full flex items-center justify-center" style="background-color: #3b82f6;">
                <svg class="w-full h-full text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </div>
              <div class="absolute -top-1 -right-1 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center shadow-md" style="background-color: #f97316;">
                <span class="text-white text-xs font-bold">${this.markerData.checkpointNumber || '?'}</span>
              </div>
            </div>
          `
        } else if (iconType === 'destination') {
          markerHTML += `
            <div class="relative">
              <div class="w-full h-full p-2 rounded-full flex items-center justify-center" style="background-color: #ef4444;">
                <svg class="w-full h-full text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                </svg>
              </div>
            </div>
          `
        } else {
          markerHTML += `
            <div class="relative">
              <div class="absolute w-10 h-10 p-2 rounded-full flex items-center justify-center" style="background-color: #a855f7;">
                <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                </svg>
              </div>
            </div>
          `
        }

        markerHTML += `
              </div>
            </div>
          </div>
        `
      }

      // Add tooltip if exists
      const hasTooltip = !!(
        this.markerData.content || this.markerData.options?.title
      )
      if (hasTooltip) {
        markerHTML += `
          <div id="overlay-tooltip-${this.options.tooltipIndex || 0}" class="overlay-tooltip absolute top-0 mt-4 bg-white text-black px-3 py-2 rounded-lg shadow-lg whitespace-nowrap z-50 pointer-events-none duration-300" >
            <div class="font-semibold text-sm">${tooltipText}</div>
            <div class="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full">
              <div class="w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-white"></div>
            </div>
          </div>
        `
      }
      markerHTML += '</div>'

      this.div.innerHTML = markerHTML
    }

    /**
     * Handle click events
     */
    handleClick(event) {
      event.stopPropagation()

      console.log('🔍 OverlayMarker clicked:', this.markerData)

      // Emit click event to parent
      if (this.options.onClick) {
        this.options.onClick(this.markerData)
      }
    }

    /**
     * Handle document click to close tooltip
     */
    handleDocumentClick() {
      // This would handle closing tooltips if needed
    }

    /**
     * Update marker data
     */
    updateMarkerData(newData) {
      this.markerData = { ...this.markerData, ...newData }

      // Ne mettre à jour le contenu que si le marker est initialisé
      if (this.div) {
        this.createMarkerContent()
        this.draw()
      } else {
        console.warn(
          "updateMarkerData appelé mais le marker n'est pas encore initialisé (this.div est null)",
        )
      }
    }

    /**
     * Show/hide marker
     */
    setVisible(visible) {
      if (this.div) {
        this.div.style.display = visible ? 'block' : 'none'
      }
    }

    /**
     * Get marker visibility
     */
    getVisible() {
      return this.div ? this.div.style.display !== 'none' : false
    }

    /**
     * Set marker z-index
     */
    setZIndex(zIndex) {
      if (this.div) {
        this.div.style.zIndex = zIndex.toString()
      }
    }
  }

  return new CustomDesignOverlayMarker(markerData, options)
}
